// src/hooks/useTrackingEvents.ts

import { track } from "@/lib/productEvents";

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

function pushToDataLayer(payload: Record<string, unknown>) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

export function trackSignUp(method: string = "email") {
  pushToDataLayer({
    event: "sign_up",
    method,
    plan: "free",
  });
}

// Nur PRO ist kaeuflich (binaeres FREE/PRO-Modell, BASIS entfaellt, siehe
// api/stripe/create-checkout-session.ts). plan ist deshalb auf "pro" fixiert.
export function trackPurchase(plan: "pro") {
  const value = 29;
  pushToDataLayer({
    event: "purchase",
    transaction_id: `${plan}_${Date.now()}`,
    currency: "EUR",
    value,
    items: [
      {
        item_id: plan,
        item_name: `Propora ${plan.toUpperCase()}`,
        price: value,
        quantity: 1,
      },
    ],
  });
}

export function trackUpgrade(from: string, to: "pro") {
  pushToDataLayer({
    event: "upgrade",
    from_plan: from,
    to_plan: to,
  });
}

/**
 * Dient zusaetzlich als Analyzer-Started-Proxy fuer Growth-Diagnostics (kein
 * eigenes analyzer_started-Event, Entscheidung 01.10. Punkt 3) -- deshalb zusaetzlich zu GA4 auch nach
 * product_events geschrieben, mit derselben Haeufigkeit wie bisher (ein
 * ToolUsageTracker-Mount in App.tsx, entprellt gegen Doppel-Feuern beim
 * gleichen Pfad).
 */
export function trackToolUsed(toolName: string) {
  pushToDataLayer({
    event: "tool_used",
    tool_name: toolName,
  });
  track("tool_used", { tool_name: toolName });
}

/**
 * Feuert einmal beim Mount einer Pricing-Ansicht. Aufruforte: Upgrade.tsx
 * (In-App-Upsell nach einem PRO-Gate) und Pricing.tsx (öffentliche /preise-Seite).
 */
export function trackPricingViewed(source: "upgrade_page" | "pricing_page", context?: string) {
  pushToDataLayer({
    event: "pricing_viewed",
    source,
    ...(context ? { context } : {}),
  });
  track("pricing_viewed", { source, ...(context ? { context } : {}) });
}

/**
 * WICHTIG: immer synchron VOR dem `window.location.href = ...`-Wechsel zur
 * Stripe-Checkout-Route aufrufen, nie danach/async -- die Seite wird direkt im
 * Anschluss verlassen (kein fetch(), echte Navigation). pushToDataLayer() ist
 * ein reiner, synchroner Array-push, GTM verarbeitet das Array unabhängig vom
 * eigenen Ladezeitpunkt (Standard-GTM-Snippet, async geladen) -- das ist exakt
 * das von Google vorgesehene Pattern für "Klick -> sofortige Weiterleitung".
 */
export function trackCheckoutStarted(plan: "pro", interval: "yearly" | "monthly") {
  pushToDataLayer({
    event: "checkout_started",
    plan,
    interval,
  });
  track("checkout_started", { plan, interval });
}

/**
 * Feuert bei jedem Klick auf einen "Jetzt upgraden"-CTA, egal ob er aus
 * ProGate.tsx (In-Page-Blur-Overlay, deckt die meisten Stellen ab) oder einer
 * der wenigen eigenständigen Upgrade-CTAs kommt (siehe Aufrufstellen).
 */
export function trackUpgradeClicked(source: string) {
  pushToDataLayer({
    event: "upgrade_clicked",
    source,
  });
  track("upgrade_clicked", { source });
}

/**
 * first_login/second_session kommen NICHT aus einem einfachen Client-Guard
 * (localStorage/sessionStorage), sondern aus der serverseitig geprüften
 * Antwort von /api/session-status -- sonst würde first_login bei jedem neuen
 * Gerät/Browser erneut feuern. Siehe SessionTracker in App.tsx.
 */
export function trackFirstLogin() {
  pushToDataLayer({ event: "first_login" });
}

export function trackSecondSession() {
  pushToDataLayer({ event: "second_session" });
}

/**
 * Activation-Funnel der Analyzer (siehe useAnalysisFunnel). Jede Analyse hat eine
 * analysis_id, damit Start, Schritte und Abschluss pro Analyse verbunden werden
 * koennen. Dedup pro analysis_id liegt im Hook, nicht hier.
 *
 * dataLayer: der fruehere localStorage-Guard ist entfernt. analysis_completed
 * feuert jetzt einmal pro Analyse statt einmal pro Browser -- die GA4-Zaehlweise
 * aendert sich ab dem Release (siehe Bericht).
 */
export function trackAnalysisStarted(analyzerType: string, analysisId: string) {
  if (typeof window === "undefined") return;
  track("analysis_started", { analyzer_type: analyzerType, analysis_id: analysisId });
  pushToDataLayer({ event: "analysis_started", analyzer_type: analyzerType });
}

export function trackAnalysisStepCompleted(analyzerType: string, analysisId: string, step: number, stepName: string) {
  if (typeof window === "undefined") return;
  track("analysis_step_completed", { analyzer_type: analyzerType, analysis_id: analysisId, step, step_name: stepName });
  pushToDataLayer({ event: "analysis_step_completed", analyzer_type: analyzerType, step, step_name: stepName });
}

export function trackAnalysisCompleted(
  analyzerType: string,
  analysisId: string,
  details: { financingOn: boolean; score?: number } = { financingOn: false }
) {
  if (typeof window === "undefined") return;
  const properties: Record<string, string | number | boolean> = {
    analyzer_type: analyzerType,
    analysis_id: analysisId,
    financing_on: details.financingOn,
  };
  if (typeof details.score === "number") properties.score = details.score;
  track("analysis_completed", properties);
  pushToDataLayer({ event: "analysis_completed", ...properties });
}

/**
 * BEWUSST NICHT VERDRAHTET (Entscheidung 2026-10-01): Die 5 Analyzer-Routen sind
 * einzelne, live berechnende Seiten ohne eigenen "Ergebnis ansehen"-Schritt --
 * "Ergebnis gesehen" waere dort technisch identisch mit dem Moment von
 * trackAnalysisCompleted() (sobald isExample=false), ein zweites Event am
 * selben Trigger haette keinen zusaetzlichen Diagnosewert und wuerde nur
 * product_events mit Duplikaten fuellen. Die Funktion bleibt fertig
 * implementiert und exportiert, falls ein Analyzer kuenftig einen echten,
 * separaten Ergebnis-Schritt bekommt (z.B. ein eigener "Ergebnis anzeigen"-Tab)
 * -- dann hier aufrufen, nicht neu bauen.
 */
/**
 * quickCheckId optional: wird nur vom Schnell-Check (/check) mitgegeben (Dominiks
 * Produkt-Events-Befund vom 12.10.2026, Dedup pro quickcheck_id ueber den ganzen
 * Funnel, siehe quickCheckFunnel.ts). Andere, bislang nicht verdrahtete Aufrufer
 * (siehe Kommentar oben) lassen das Feld einfach weg.
 */
export function trackResultViewed(analyzerType: string, quickCheckId?: string) {
  const properties: Record<string, string> = { analyzer_type: analyzerType };
  if (quickCheckId) properties.quickcheck_id = quickCheckId;
  pushToDataLayer({ event: "result_viewed", ...properties });
  track("result_viewed", properties);
}

/**
 * Gefuehrter Ablauf nach /check (PHASE-4-BEFUND.md Runde 1, /deal/:id): ein
 * Hebel-/Vertiefen-/Entscheiden-Bereich wurde geoeffnet. stepName z. B.
 * "hebel_finanzierungscheck"/"hebel_optimierer" -- je eigener Wert pro Bereich, trotz
 * gemeinsamer Rechen-Komponente (Schritt 4).
 */
export function trackDealFlowStepViewed(stepName: string, dealId: string, analyzerType: string) {
  const properties = { step_name: stepName, deal_id: dealId, analyzer_type: analyzerType };
  pushToDataLayer({ event: "deal_flow_step_viewed", ...properties });
  track("deal_flow_step_viewed", properties);
}

/**
 * Einzelne Interaktion innerhalb eines Hebel-/Vertiefen-/Entscheiden-Bereichs (z. B.
 * "Naechstes Objekt pruefen" angeklickt, Optimierer-Slider bewegt). action ist
 * Freitext-artig, aber kein PII (z. B. "naechstes_objekt_geklickt").
 */
export function trackDealFlowActionClicked(stepName: string, action: string, dealId: string, analyzerType: string) {
  const properties = { step_name: stepName, action, deal_id: dealId, analyzer_type: analyzerType };
  pushToDataLayer({ event: "deal_flow_action_clicked", ...properties });
  track("deal_flow_action_clicked", properties);
}

/**
 * Formular-Interaktion auf /register (erstes Feld fokussiert, noch nicht
 * abgeschickt) -- siehe Register.tsx. Bewusst NUR GA4 (dataLayer), KEIN
 * product_events: zu diesem Zeitpunkt existiert noch kein Clerk-User, also
 * keine user_id, an die sich ein Produkt-Event haengen liesse.
 */
export function trackSignupStarted() {
  pushToDataLayer({ event: "signup_started" });
}

/**
 * E5 (18.10.2026): Exposé-Upload im Schnell-Check. stage: "gestartet" | "erfolgreich" |
 * "fehlgeschlagen" | "registrieren_geklickt"; felder = Anzahl übernommener Felder.
 */
export function trackExposeUpload(stage: string, objektart: string, felder?: number) {
  // GA4: eigenes Event mit Details. product_events: vorhandenes "tool_used" (tool_name +
  // action sind serverseitig erlaubt) -- kein neues Event, also keine SQL-Änderung nötig.
  pushToDataLayer({ event: "expose_upload", stage, analyzer_type: objektart, ...(felder != null ? { fields_found: felder } : {}) });
  track("tool_used", { tool_name: "expose_upload", action: stage, analyzer_type: objektart });
}

/** Ladezeit Objektseite (18.10.2026): einmal je Aufruf, sobald die Kennzahlen stehen.
 *  ms_seit_oeffnen = ab Mount der Seite; ms_seit_seitenaufruf = ab Laden des Browser-Tabs
 *  (nur aussagekräftig beim Direkteinstieg). Nur GA4/dataLayer, keine product_events (kein SQL). */
export function trackObjektseiteGeladen(werte: { msSeitOeffnen: number; msSeitSeitenaufruf: number; objektAusListe: boolean; objektart: string }) {
  pushToDataLayer({
    event: "objektseite_geladen",
    ms_seit_oeffnen: Math.round(werte.msSeitOeffnen),
    ms_seit_seitenaufruf: Math.round(werte.msSeitSeitenaufruf),
    objekt_aus_liste: werte.objektAusListe,
    analyzer_type: werte.objektart,
  });
}
